import { cn } from "@/lib/utils";
import { SG7Badge } from "./SG7Badge";

interface SG7ConfidenceMeterProps {
  value: number; // 0-100
  riskLevel?: 'low' | 'medium' | 'high';
  edgeValue?: number;
  isLive?: boolean; // Only pulse when live
  className?: string;
}

export function SG7ConfidenceMeter({ 
  value, 
  riskLevel,
  edgeValue,
  isLive = false,
  className 
}: SG7ConfidenceMeterProps) {
  const clampedValue = Math.max(0, Math.min(100, value));
  
  // Determine confidence level based on value
  const getConfidenceLevel = (): 'high' | 'medium' | 'low' => {
    if (clampedValue >= 75) return 'high';
    if (clampedValue >= 50) return 'medium';
    return 'low';
  };

  const confidenceLevel = getConfidenceLevel();

  // Dot color based on confidence level
  const getDotColor = () => {
    switch (confidenceLevel) {
      case 'high': return 'bg-[hsl(var(--sg7-confidence-high))]';
      case 'medium': return 'bg-[hsl(var(--sg7-confidence-medium))]';
      case 'low': return 'bg-[hsl(var(--sg7-confidence-low))]';
    }
  };

  // Infer risk level from confidence if not provided
  const inferredRiskLevel = riskLevel || (
    confidenceLevel === 'high' ? 'low' : 
    confidenceLevel === 'low' ? 'high' : 'medium'
  );

  return (
    <div className={cn("space-y-3", className)}>
      {/* Progress bar row */}
      <div className="flex items-center gap-3">
        {/* Status dot - only pulse when LIVE */}
        <div className="relative flex items-center justify-center">
          <span 
            className={cn(
              "h-3 w-3 rounded-full",
              getDotColor(),
              isLive && "animate-pulse"
            )}
          />
          {isLive && (
            <span 
              className={cn(
                "absolute h-3 w-3 rounded-full opacity-40",
                getDotColor(),
                "animate-ping"
              )}
            />
          )}
        </div>

        {/* Percentage */}
        <span className="text-lg font-black text-[hsl(var(--sg7-text))] tabular-nums min-w-[48px]">
          {clampedValue}%
        </span>

        {/* Progress bar */}
        <div className="flex-1 h-[6px] bg-[hsl(var(--sg7-surface-3))] rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-700 ease-out bg-gradient-to-r from-[hsl(var(--sg7-gold))] to-[hsl(var(--sg7-gold)/0.7)]"
            style={{ width: `${clampedValue}%` }}
          />
        </div>
      </div>

      {/* Badges row */}
      <div className="flex flex-wrap items-center gap-2">
        <SG7Badge 
          variant="confidence" 
          level={confidenceLevel} 
        />
        <SG7Badge 
          variant="risk" 
          level={inferredRiskLevel} 
        />
        {typeof edgeValue === 'number' && edgeValue > 0 && (
          <SG7Badge 
            variant="edge" 
            value={edgeValue.toFixed(1)} 
          />
        )}
      </div>
    </div>
  );
}
